import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { GameActions } from './GameActions';
import { UIReducer } from '../UI/UIReducer';
import { LevelReducer } from '../Level/LevelReducer';
import { PlayerReducer } from '../Player/PlayerReducer';
import { WorldReducer } from '../World/WorldReducer';
import { LuckyTurnipsReducer } from '../LuckyTurnips/LuckyTurnipsReducer';
import { FarmReducer } from '../Farm/FarmReducer';
import GameStateHelper from './GameStateHelper';
import { TimerReducer } from '../Timer/TimerReducer';
import { AsyncActionStatus } from '../AsyncAction';
import { HelperFriendReducer } from '../Level/HelperFriend/HelperFriendReducer';
import { getISOTime } from 'Utility/DateHelper';
import { GameScene } from './GameScene';
import { ContextType } from './ContextType';
import { IGameState } from './IGameState';
import { initialContext, initialState } from './initialState';
import { unexpectedValue } from 'Utility/Types';
import { MyPetMiniGameReducer } from '../MyPetMiniGame/MyPetMiniGameReducer';
import ContextStateHelper from './Context/ContextStateHelper';
import { DataConflictReducer } from './DataConflictReducer';
import { PlatformReducer } from './PlatformReducer';
import { SetAdDebugMode } from 'State/Game/AdDebugMode';
import { PlinkoReducer } from 'State/Plinko/PlinkoReducer';
import { SetIAPDebugMode } from './IAPDebugMode';

export const gameReducer: Reducer<IGameState> = (
    state: IGameState = initialState,
    action: ActionType<typeof GameActions>
): IGameState => {
    if (action.type === getType(GameActions.enableIAP)) {
        return GameStateHelper.enableIAP(state, action.payload);
    }

    if (action.type === getType(GameActions.restoreContext) && state.context.current !== ContextType.DEFAULT) {
        return ContextStateHelper.restoreContext(state, {
            status: action.payload.status,
            responseType: action.payload.type,
            data: action.payload.data,
        });
    }

    state = HelperFriendReducer(state, action);
    state = DataConflictReducer(state, action);

    state = prioGameReducer(state, action);

    if (state.gamePaused) {
        return state;
    }

    switch (action.type) {
        case getType(GameActions.setState):
            return action.payload;
        case getType(GameActions.setPerSessionData):
            return {
                ...state,
                perSessionData: {
                    ...state.perSessionData,
                    ...action.payload,
                },
            };
        case getType(GameActions.setContext):
            if (state.gameLoaded && !state.gamePaused) {
                return ContextStateHelper.setContext(state, action.payload.context, action.payload.data);
            }
            return state;
        case getType(GameActions.clearContextData):
            if (state.gameLoaded) {
                return {
                    ...state,
                    context: initialContext,
                };
            }
            return state;
        case getType(GameActions.loadWorld):
            return GameStateHelper.loadWorld(state, action.payload);
        case getType(GameActions.loadLevel):
            if (
                state.activeScene.status === AsyncActionStatus.EMPTY ||
                state.activeScene.status === AsyncActionStatus.COMPLETE
            ) {
                return GameStateHelper.loadLevelOrShowNoCoinsPopup(state, action.payload);
            }
            break;
        case getType(GameActions.loadLevelDebug):
            return GameStateHelper.loadLevel(state, { levelNumber: 99, preBoosters: [], debugLevelData: action.payload});
        case getType(GameActions.loadLuckyTurnips):
            return GameStateHelper.loadLuckyTurnips(state);
        case getType(GameActions.loadMyPetMiniGame):
            return GameStateHelper.loadMyPetMiniGame(state, action.payload);
        case getType(GameActions.loadFarmScene):
            return GameStateHelper.loadFarmScene(state, action.payload);
        case getType(GameActions.loadPlinkoScene):
            return GameStateHelper.loadPlinkoScene(state, action.payload);
        case getType(GameActions.restartLevel):
            if (
                state.activeScene.status === AsyncActionStatus.COMPLETE &&
                state.activeScene.result === GameScene.LEVEL
            ) {
                return GameStateHelper.loadLevelOrShowNoCoinsPopup(state, {
                    levelNumber: action.payload,
                    restart: true,
                    preBoosters: [],
                });
            }
            break;
        case getType(GameActions.endLevel):
            return GameStateHelper.endLevel(state, action.payload);

        case getType(GameActions.setSceneLoaded):
            if (state.activeScene.status === AsyncActionStatus.IN_PROGRESS) {
                return {
                    ...state,
                    activeScene: {
                        ...state.activeScene,
                        status: AsyncActionStatus.COMPLETE,
                    },
                };
            }
            try {
                console.log(`[${getISOTime()}] Loaded`);
            } catch (err) {
                /* who cares? */
            }
            return state;

        case getType(GameActions.setSceneLoading):
            if (state.activeScene.status === AsyncActionStatus.QUEUED) {
                return {
                    ...state,
                    activeScene: {
                        ...state.activeScene,
                        status: AsyncActionStatus.IN_PROGRESS,
                    },
                };
            }
            return state;
    }

    state = {
        ...state,
        ...PlayerReducer(state, action),
    };

    state = {
        ...state,
        UIState: UIReducer(state.UIState, action),
    };

    if (state.activeScene.status === AsyncActionStatus.COMPLETE) {
        switch (state.activeScene.result) {
            case GameScene.LEVEL:
                state = LevelReducer(state, action);
                break;
            case GameScene.WORLD:
                state = WorldReducer(state, action);
                break;
            case GameScene.LUCKY_TURNIPS:
                state = LuckyTurnipsReducer(state, action);
                break;
            case GameScene.MY_PET_MINI_GAME:
                state = MyPetMiniGameReducer(state, action);
                break;
            case GameScene.FARM:
                state = FarmReducer(state, action);
                break;
            case GameScene.PLINKO:
                state = PlinkoReducer(state, action);
                break;
            default:
                unexpectedValue(state.activeScene.result);
        }
    }

    state = TimerReducer(state, action);
    state = PlatformReducer(state, action);

    return state;
};

const prioGameReducer: Reducer<IGameState> = (
    state: IGameState = initialState,
    action: ActionType<typeof GameActions>
): IGameState => {
    switch (action.type) {
        case getType(GameActions.gameLoaded):
            return {
                ...state,
                gameLoaded: true,
            };
        case getType(GameActions.setLoadedData):
            return GameStateHelper.gameLoaded(state, action.payload);
        case getType(GameActions.setCanvasSize):
            return {
                ...state,
                canvasSize: action.payload,
            };
        case getType(GameActions.setCrossPromo):
            return {
                ...state,
                userState: {
                    ...state.userState,
                    crossPromo: action.payload.crossPromoStatus,
                },
                playerState: action.payload.playerState,
            };
        case getType(GameActions.setAdDebugMode):
            return SetAdDebugMode(state, action.payload);
        case getType(GameActions.setIAPDebugMode):
            return SetIAPDebugMode(state, action.payload);
        case getType(GameActions.connectedToGBC):
            return GameStateHelper.reconnectedToGBC(state);
        case getType(GameActions.disconnectedFromGBC):
            return GameStateHelper.disconnectedFromGBC(state);
        case getType(GameActions.setGamePaused):
            return GameStateHelper.setGamePaused(state, action.payload);
    }

    return state;
};
